import React, { useEffect, useState, useContext } from "react";
import { 
  Table, 
  Alert, 
  Container, 
  Card, 
  Button, 
  Badge, 
  Form, 
  Row, 
  Col,
  Collapse 
} from "react-bootstrap";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import { Input } from "reactstrap";

const CDRStatistics = () => {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    
    const [hourlyStats, setHourlyStats] = useState([]);
    const [timeFrameStats, setTimeFrameStats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    
    // Advanced search filters
    const [filters, setFilters] = useState({
        direction: "",
        callerName: "",
        extension: "",
        destination: "",
        context: "",
        startRangeFrom: "",
        startRangeTo: "",
        answerRangeFrom: "",
        answerRangeTo: "",
        endRangeFrom: "",
        endRangeTo: "",
        durationMin: "",
        durationMax: "",
        billSeconds: "",
        hangupCause: "",
        uuid: "",
        bridgeUuid: "",
        readCodec: "",
        writeCodec: "",
        remoteMediaIp: "",
        networkAddress: "",
        mosComparison: "",
        mosScore: "",
        ringGroup: "",
        showAll: false,
    });
    
    const [useExtensionDropdown, setUseExtensionDropdown] = useState(true);
    const [extensions, setExtensions] = useState([]);
    const [ringGroups, setRingGroups] = useState([]);

    // Fetch extensions for dropdown
    const fetchExtensions = async () => {
        try {
            const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
            const site_uuid = currentSite?.site_uuid || null;
            const res = await fetch(`${serverHost}/get_rows?table=v_extensions&pageSize=50&page=1&domain_uuid=${encodeURIComponent(site_uuid)}`);
            if (res.ok) {
                const data = await res.json();
                const extensions = (data.results).map(e => ({
                extension_uuid: e.extension_uuid,
                name: e.extension,
                accountcode: e.accountcode
                }));
                console.log("extensions: ", extensions)
                setExtensions(extensions);
            }
        } catch (err) {
            console.error("Failed to fetch extensions:", err);
        }
    };

    // Fetch CDR statistics
    const fetchCDRStatistics = async () => {
        setLoading(true);
        setError(null);
        try {
            const site = JSON.parse(localStorage.getItem("selectedSite"));
            const payload = {
                filters: filters,
            };
            
            const response = await fetch(`${serverHost}/cdr_stats/${site.site_uuid}`, {
                method: "POST",
                headers: {
                'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });
            
            if (!response.ok) throw new Error("Failed to fetch CDR statistics");
            const data = await response.json();
            console.log(data.result)
            // Separate hourly and daily stats based on s_hour value
            const hourly = data.result.filter(stat => stat.s_hour === 1);
            const timeframe = data.result.filter(stat => stat.s_hour === 168 || stat.s_hour === 24 || stat.s_hour === 720);
            setHourlyStats(hourly || []);
            setTimeFrameStats(timeframe || []);
            console.log("CDR statistics loaded");
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExtensions();
        fetchCDRStatistics();
    }, []);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
        ...prev,
        [field]: value
        }));
    };

    const handleApplyFilters = () => {
        fetchCDRStatistics();
    };

    const handleReset = () => {
        setFilters({
            direction: "",
            callerName: "",
            extension: "",
            destination: "",
            context: "",
            startRangeFrom: "",
            startRangeTo: "",
            answerRangeFrom: "",
            answerRangeTo: "",
            endRangeFrom: "",
            endRangeTo: "",
            durationMin: "",
            durationMax: "",
            billSeconds: "",
            hangupCause: "",
            uuid: "",
            bridgeUuid: "",
            readCodec: "",
            writeCodec: "",
            remoteMediaIp: "",
            networkAddress: "",
            mosComparison: "",
            mosScore: "",
            ringGroup: "",
            showAll: false,
        });
        fetchCDRStatistics();
    };

    const downloadCSV = (data, filename) => {
        const headers = [
        "Hours/Days",
        "Date",
        "Time",
        "Volume",
        "Minutes",
        "Calls Per Min",
        "Missed",
        "ASR",
        "ALOC"
        ];
        
        const rows = data.map(stat => [
        stat.hours || stat.days || "",
        stat.date || "",
        stat.time || "",
        stat.volume || 0,
        stat.minutes || 0,
        stat.calls_per_minute?.toFixed(2) || 0,
        stat.missed || 0,
        stat.asr || 0,
        stat.aloc || 0
        ]);
        
        const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
        ].join("\n");
        
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderTable = (data, title, type) => (
        <Card className={`shadow-sm mb-4 ${isDark ? "bg-dark text-light border-dark" : ""}`}>
        <Card.Header
            className="d-flex justify-content-between align-items-center flex-wrap"
            style={{
            backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
            color: isDark ? "#e2e8f0" : "#111",
            }}
        >
            <h4 className="mb-2 mb-md-0">
            {title}
            </h4>
            <Button
            variant="success"
            size="sm"
            onClick={() => downloadCSV(data, type)}
            disabled={data.length === 0}
            >
            Download CSV
            </Button>
        </Card.Header>

        <Card.Body style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}>
            {data.length === 0 ? (
            <div
                className="text-center py-5"
                style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
            >
                <h5>No statistics found</h5>
                <p className="mb-0">There are currently no statistics for the selected period.</p>
            </div>
            ) : (
            <div style={{ overflowX: "auto" }}>
                <Table
                striped
                hover
                responsive
                bordered
                className="mb-0"
                style={{
                    backgroundColor: isDark ? "#1f2937" : "#fff",
                    color: isDark ? "#e2e8f0" : "#212529",
                }}
                >
                <thead
                    style={{
                    backgroundColor: isDark ? "#374151" : "#f8f9fa",
                    fontWeight: "bold",
                    color: isDark ? "#e2e8f0" : "#212529",
                    }}
                >
                    <tr>
                    <th className="text-center">{type === "hourly" ? "Hours" : "Days"}</th>
                    <th className="text-center">Date</th>
                    <th className="text-center">Time</th>
                    <th className="text-center">Volume</th>
                    <th className="text-center">Minutes</th>
                    <th className="text-center">Calls Per Min</th>
                    <th className="text-center">Missed</th>
                    <th className="text-center">ASR</th>
                    <th className="text-center">ALOC</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((stat, index) => (
                    <tr key={index} style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        <td className="text-center" style={{ fontWeight: 600 }}>
                        {stat.hours || "-"}
                        </td>
                        <td className="text-center">{stat.date || "-"}</td>
                        <td className="text-center" style={{ fontFamily: "monospace" }}>
                        {stat.time || "-"}
                        </td>
                        <td className="text-center">
                        <Badge bg="primary">{stat.volume || 0}</Badge>
                        </td>
                        <td className="text-center" style={{ fontWeight: 500 }}>
                        {stat.minutes || 0}
                        </td>
                        <td className="text-center" style={{ fontFamily: "monospace" }}>
                        {Number(stat.calls_per_minute)?.toFixed(2) || "0.00"}
                        </td>
                        <td className="text-center">
                            {stat.missed || 0}
                        </td>
                        <td className="text-center">
                            {stat.asr || 0}%
                        </td>
                        <td className="text-center" style={{ fontFamily: "monospace", fontWeight: 500 }}>
                        {stat.aloc || 0}
                        </td>
                    </tr>
                    ))}
                </tbody>
                </Table>
            </div>
            )}
        </Card.Body>
        </Card>
    );

    return (
        <>
        <SimpleHeader name="CDR Statistics" parentName="HUCS" />

        <Container fluid className="mt--6">
            {/* Advanced Search Card */}
            <Card className={`shadow-sm mb-3 ${isDark ? "bg-dark text-light border-dark" : ""}`}>
            <Card.Header
                style={{
                backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                color: isDark ? "#e2e8f0" : "#111",
                cursor: "pointer",
                }}
                onClick={() => setShowAdvanced(!showAdvanced)}
            >
                <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Advanced Search</h5>
                <Button
                    variant="link"
                    style={{ color: isDark ? "#60a5fa" : "#0d6efd" }}
                    className="p-0"
                >
                    {showAdvanced ? "Hide" : "Show"}
                </Button>
                </div>
            </Card.Header>
            <Collapse in={showAdvanced}>
                <Card.Body className="page-menu" style={{ backgroundColor: isDark ? "#1f2937" : "#fff"}}>
                <Row className="g-3">
                    {/* Direction */}
                    <Col md={3}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Direction
                    </Form.Label>
                    <Input
                        type="select"
                        value={filters.direction}
                        onChange={(e) => handleFilterChange("direction", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    >
                        <option value="">All Directions</option>
                        <option value="inbound">Inbound</option>
                        <option value="outbound">Outbound</option>
                        <option value="local">Local</option>
                    </Input>
                    </Col>

                    {/* Caller Name */}
                    <Col md={3}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Caller Name
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter caller name"
                        value={filters.callerName}
                        onChange={(e) => handleFilterChange("callerName", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* Extension */}
                    <Col md={3}>
                    <Form.Label className="d-block" style={{ 
                        color: isDark ? "#e2e8f0" : "#212529", 
                    }}>
                        Extension  
                    <Form.Check
                        type="switch"
                        id="extension-toggle"
                        label="Text"
                        checked={!useExtensionDropdown}
                        onChange={(e) => setUseExtensionDropdown(!e.target.checked)}
                        className="d-inline ms-2"
                        style={{ 
                            fontSize: "0.875rem", 
                            paddingLeft: "3rem"
                        }}
                    />
                    </Form.Label>
                    {useExtensionDropdown ? (
                        <Input
                        type = "select"
                        value={filters.extension}
                        onChange={(e) => handleFilterChange("extension", e.target.value)}
                        style={{
                            backgroundColor: isDark ? "#374151" : "#fff",
                            color: isDark ? "#e2e8f0" : "#212529",
                            borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                        >
                        <option value="">All Extensions</option>
                        {extensions.map((ext, idx) => (
                            <option key={idx} value={ext.name}>
                            {ext.name}
                            </option>
                        ))}
                        </Input>
                    ) : (
                        <Form.Control
                        type="text"
                        placeholder="Enter extension"
                        value={filters.extension}
                        onChange={(e) => handleFilterChange("extension", e.target.value)}
                        style={{
                            backgroundColor: isDark ? "#374151" : "#fff",
                            color: isDark ? "#e2e8f0" : "#212529",
                            borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                        />
                    )}
                    </Col>

                    {/* Destination */}
                    <Col md={3}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Destination
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter destination"
                        value={filters.destination}
                        onChange={(e) => handleFilterChange("destination", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* Context */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Context
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter context"
                        value={filters.context}
                        onChange={(e) => handleFilterChange("context", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* Start Range From */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Start Range From
                    </Form.Label>
                    <Form.Control
                        type="datetime-local"
                        value={filters.startRangeFrom}
                        onChange={(e) => handleFilterChange("startRangeFrom", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* Start Range To */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Start Range To
                    </Form.Label>
                    <Form.Control
                        type="datetime-local"
                        value={filters.startRangeTo}
                        onChange={(e) => handleFilterChange("startRangeTo", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* Answer Range From */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Answer Range From
                    </Form.Label>
                    <Form.Control
                        type="datetime-local"
                        value={filters.answerRangeFrom}
                        onChange={(e) => handleFilterChange("answerRangeFrom", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* Answer Range To */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Answer Range To
                    </Form.Label>
                    <Form.Control
                        type="datetime-local"
                        value={filters.answerRangeTo}
                        onChange={(e) => handleFilterChange("answerRangeTo", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* End Range From */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        End Range From
                    </Form.Label>
                    <Form.Control
                        type="datetime-local"
                        value={filters.endRangeFrom}
                        onChange={(e) => handleFilterChange("endRangeFrom", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* End Range To */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        End Range To
                    </Form.Label>
                    <Form.Control
                        type="datetime-local"
                        value={filters.endRangeTo}
                        onChange={(e) => handleFilterChange("endRangeTo", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* Duration Min */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Duration Min (sec)
                    </Form.Label>
                    <Form.Control
                        type="number"
                        placeholder="Min"
                        value={filters.durationMin}
                        onChange={(e) => handleFilterChange("durationMin", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* Duration Max */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Duration Max (sec)
                    </Form.Label>
                    <Form.Control
                        type="number"
                        placeholder="Max"
                        value={filters.durationMax}
                        onChange={(e) => handleFilterChange("durationMax", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* UUID */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        UUID
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter UUID"
                        value={filters.uuid}
                        onChange={(e) => handleFilterChange("uuid", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* Bill Seconds */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Bill Seconds
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter bill seconds"
                        value={filters.billSeconds}
                        onChange={(e) => handleFilterChange("billSeconds", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* Hangup Cause */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Hangup Cause
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter hangup cause"
                        value={filters.hangupCause}
                        onChange={(e) => handleFilterChange("hangupCause", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    

                    {/* Bridge UUID */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Bridge UUID
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter bridge UUID"
                        value={filters.bridgeUuid}
                        onChange={(e) => handleFilterChange("bridgeUuid", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* Read Codec */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Read Codec
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter read codec"
                        value={filters.readCodec}
                        onChange={(e) => handleFilterChange("readCodec", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* Write Codec */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Write Codec
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter write codec"
                        value={filters.writeCodec}
                        onChange={(e) => handleFilterChange("writeCodec", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* Remote Media IP */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Remote Media IP
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter remote media IP"
                        value={filters.remoteMediaIp}
                        onChange={(e) => handleFilterChange("remoteMediaIp", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* Network Address */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Network Address
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter network address"
                        value={filters.networkAddress}
                        onChange={(e) => handleFilterChange("networkAddress", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* MOS Score Comparison */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label class="d-block" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        MOS Comparison
                    </Form.Label>
                    <Input
                        type="select"
                        value={filters.mosComparison}
                        onChange={(e) => handleFilterChange("mosComparison", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    >
                        <option value="">Select</option>
                        <option value="=">=</option>
                        <option value="<">&lt;</option>
                        <option value="<=">&lt;=</option>
                        <option value=">">&gt;</option>
                        <option value=">=">&gt;=</option>
                    </Input>
                    </Col>

                    {/* MOS Score */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        MOS Score
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Score"
                        value={filters.mosScore}
                        onChange={(e) => handleFilterChange("mosScore", e.target.value)}
                        style={{
                        backgroundColor: isDark ? "#374151" : "#fff",
                        color: isDark ? "#e2e8f0" : "#212529",
                        borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    />
                    </Col>

                    {/* Ring Group */}
                    <Col md={3} style={{marginTop: "1rem",}}>
                    <Form.Label class="d-block" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                        Ring Group
                    </Form.Label>
                    <Input
                        type="select"
                        value={filters.ringGroup}
                        onChange={(e) => handleFilterChange("ringGroup", e.target.value)}
                        style={{
                            backgroundColor: isDark ? "#374151" : "#fff",
                            color: isDark ? "#e2e8f0" : "#212529",
                            borderColor: isDark ? "#4b5563" : "#ced4da",
                        }}
                    >
                        <option value="">All Ring Groups</option>
                        {ringGroups.map((group, idx) => (
                        <option key={idx} value={group.ring_group_uuid}>
                            {group.ring_group_name} - {group.ring_group_extension}
                        </option>
                        ))}
                    </Input>
                    </Col>

                    {/* Show All */}
                    <Col md={3} style={{marginTop: "1rem",}} className="d-flex align-items-end">
                    <Form.Check
                        type="checkbox"
                        id="show-all"
                        label="Show All"
                        checked={filters.showAll}
                        onChange={(e) => handleFilterChange("showAll", e.target.checked)}
                        style={{ color: isDark ? "#e2e8f0" : "#212529" }}
                    />
                    </Col>
                </Row>

                <Row className="mt-3">
                    <Col>
                    <Button
                        variant="primary"
                        onClick={handleApplyFilters}
                        disabled={loading}
                        className="me-2"
                    >
                        {loading ? "Loading..." : "Apply Filters"}
                    </Button>
                    <Button
                        variant="light"
                        onClick={handleReset}
                    >
                        Reset
                    </Button>
                    <Button
                        variant="info"
                        className="ms-2"
                        onClick={fetchCDRStatistics}
                        disabled={loading}
                    >
                        {loading ? "Refreshing..." : "Refresh"}
                    </Button>
                    </Col>
                </Row>
                </Card.Body>
            </Collapse>
            </Card>

            {/* Error Alert */}
            {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
            </Alert>
            )}

            {/* Hourly Statistics Table */}
            {renderTable(hourlyStats, "HOURLY STATISTICS", "hourly")}

            {/* Daily Statistics Table */}
            {renderTable(timeFrameStats, "INTERVAL STATISTICS", "daily")}
        </Container>
        </>
    );
};

export default CDRStatistics;